/*
 * BetterFX's hidden effects, on the BetterFX page (sections/betterfx-hide.liquid).
 *
 * On one side the floating search bar with "blur" typed, on the other the
 * BetterFX panel as it sits in Premiere. The bar's results are the plugin's
 * own: the same filter as betterfx-desktop/src/main.ts (hidden effects and,
 * with "Show in search results" off under Legacy Effects, every effect whose
 * name ends in "(legacy)" - isLegacyEffectName in BetterFX CEP/js/bridge-core.js
 * - are left out before ranking), the same search and ranking (search.ts and
 * ranking.ts, ported as in assets/betterfx-try.js), the same category rows
 * and tags.
 *
 * While on screen a loop plays: in the panel's General tab the pointer clicks
 * the eye on three blurs you never use (they leave the list and wait in the
 * Hidden tab), opens the Search tab and switches legacy effects off, then
 * shows the Hidden tab. Each click re-ranks the bar, its rows slide into
 * place, and the count under it shows the real numbers. Any touch hands it
 * over: click an eye, flip a Search tab switch (legacy, custom
 * presets, third-party effects or their tags), unhide from the Hidden tab, and the
 * bar follows live. Off screen or in a hidden tab nothing runs, and it starts
 * over next time. With reduced motion it shows the finished, clean result
 * and is handed over at once.
 */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const STOP = {};
  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

  /* ---------- The plugin's search (search.ts) ---------- */
  const normalizeSearch = (value) =>
    value
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  const searchWords = (value) => normalizeSearch(value).split(' ').filter(Boolean);
  const searchInitials = (value) => searchWords(value).map((w) => w[0] || '').join('');

  const scoreSubsequence = (query, text) => {
    let qi = 0;
    let prev = -1;
    let gap = 0;
    let run = 0;
    for (let ti = 0; ti < text.length; ti += 1) {
      if (text[ti] !== query[qi]) continue;
      if (prev >= 0 && ti === prev + 1) run += 25;
      else if (prev >= 0) gap += ti - prev - 1;
      else gap += ti;
      prev = ti;
      qi += 1;
      if (qi === query.length) return 5000 + run - gap * 10 - text.length;
    }
    return -1;
  };

  const scoreMatch = (query, text) => {
    const q = normalizeSearch(query);
    const t = normalizeSearch(text);
    if (!q.length) return 100;
    if (t === q) return 10000;
    if (t.startsWith(q)) return 9000 - t.length;
    const words = searchWords(t);
    const wi = words.findIndex((w) => w.startsWith(q));
    if (wi >= 0) return 8500 - wi * 50 - words[wi].length;
    const initials = searchInitials(t);
    if (initials === q) return 8300;
    if (initials.startsWith(q)) return 8200 - initials.length;
    const ci = t.indexOf(q);
    if (ci >= 0) return 7000 - ci * 10 - t.length;
    return scoreSubsequence(q, t);
  };

  const fuzzySearch = (items, query) =>
    items
      .map((item, index) => ({ item, index, score: scoreMatch(query, item.name) }))
      .filter((r) => r.score >= 0)
      .sort((a, b) => (b.score !== a.score ? b.score - a.score : a.index - b.index))
      .map((r) => r.item);

  /* ---------- The plugin's ranking (ranking.ts) ---------- */
  const normalizeRank = (value) => value.toLocaleLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').trim();
  const rankWords = (value) => normalizeRank(value).split(/[\s\-_/\\()[\].]+/).filter(Boolean);
  const rankInitials = (value) => rankWords(value).map((w) => w[0] || '').join('');

  const queryBonus = (name, query) => {
    const n = normalizeRank(name);
    const q = normalizeRank(query);
    if (!q) return 0;
    if (n === q) return 100000;
    if (n.startsWith(q)) return 80000;
    const words = rankWords(name);
    if (words.some((w) => w === q)) return 70000;
    if (words.some((w) => w.startsWith(q))) return 60000;
    const initials = rankInitials(name);
    if (initials === q) return 55000;
    if (initials.startsWith(q)) return 50000;
    if (n.includes(q)) return 40000;
    return 0;
  };

  const KIND_ORDER = { preset: 0, video: 1, audio: 2 };

  const rankEffects = (effects, rawQuery, meta) => {
    const query = rawQuery.trim();
    const fuzzy = fuzzySearch(effects, query);
    const ranked = fuzzy.map((effect, fuzzyIndex) => {
      const recentIndex = meta.recents.indexOf(effect.name);
      const uses = meta.usage[effect.name] || 0;
      const score =
        queryBonus(effect.name, query) +
        Math.max(0, fuzzy.length - fuzzyIndex) * 1000 +
        (meta.favorites.includes(effect.name) ? 12000 : 0) +
        (recentIndex >= 0 ? Math.max(0, 5000 - recentIndex * 250) : 0) +
        (uses > 0 ? Math.min(Math.log2(uses + 1) * 40, 2000) : 0);
      return { effect, score, fuzzyIndex };
    });
    ranked.sort((a, b) => {
      if (a.score !== b.score) return b.score - a.score;
      if (a.fuzzyIndex !== b.fuzzyIndex) return a.fuzzyIndex - b.fuzzyIndex;
      if (a.effect.kind !== b.effect.kind) return KIND_ORDER[a.effect.kind] - KIND_ORDER[b.effect.kind];
      return a.effect.name.localeCompare(b.effect.name, undefined, { sensitivity: 'base', numeric: true });
    });
    return ranked.map((r) => r.effect);
  };

  const CATEGORY = { video: 'VIDEO EFFECTS', audio: 'AUDIO EFFECTS', preset: 'CUSTOM PRESETS' };

  // isLegacyEffectName and the third-party suites, from bridge-core.js
  const LEGACY = /\(\s*legacy\s*\)\s*$/i;
  const SUITES = [/^BCC\b/i, /^(S_|Sapphire\b)/i];

  /* ---------- What this Premiere has that "blur" finds ----------
     Custom presets come first, then the installed effects, the way main.ts
     builds its list. Premiere renames an effect it has replaced with
     "(Legacy)" at the end, like the old Gaussian Blur. */
  const QUERY = 'blur';
  const CATALOG = [
    { name: 'Blur Push In', kind: 'preset' },
    { name: 'Camera Blur', kind: 'video' },
    { name: 'Channel Blur', kind: 'video' },
    { name: 'Compound Blur', kind: 'video' },
    { name: 'Directional Blur', kind: 'video' },
    { name: 'Gaussian Blur', kind: 'video' },
    { name: 'Gaussian Blur (Legacy)', kind: 'video' },
    { name: 'S_Blur', kind: 'video' },
  ].map((e) => ({ ...e, isLegacy: LEGACY.test(e.name), isThirdParty: SUITES.some((p) => p.test(e.name)) }));
  const META = {
    recents: ['Gaussian Blur', 'S_Blur', 'Blur Push In'],
    usage: { 'Gaussian Blur': 23, 'S_Blur': 7, 'Directional Blur': 5, 'Blur Push In': 4 },
    favorites: [],
  };
  // the blurs this editor never uses: hidden in the loop
  const NEVER = ['Camera Blur', 'Channel Blur', 'Compound Blur'];

  // main.ts: hidden and (with the switch off) legacy effects are filtered out,
  // the rest is ranked, and the leading kind's results come first
  // and so are custom presets and third-party effects when their switches are off
  const ALL = { legacy: true, presets: true, third: true };
  const results = (hidden, show) => {
    const searchable = CATALOG.filter(
      (e) =>
        !hidden.has(e.name) &&
        !(e.isLegacy && !show.legacy) &&
        !(e.kind === 'preset' && !show.presets) &&
        !(e.isThirdParty && !show.third)
    );
    const ranked = rankEffects(searchable, QUERY, META);
    const lead = ranked[0] && ranked[0].kind;
    return lead ? [...ranked.filter((e) => e.kind === lead), ...ranked.filter((e) => e.kind !== lead)] : [];
  };

  const init = (root) => {
    if (root.dataset.bhReady) return;
    root.dataset.bhReady = 'true';

    const q = (sel) => root.querySelector(sel);
    const qa = (sel) => [...root.querySelectorAll(sel)];
    const stage = q('[data-bh-stage]');
    const scroller = q('[data-bh-scroll]');
    const list = q('[data-bh-list]');
    if (!stage || !scroller || !list) return;
    const countEl = q('[data-bh-count]');
    const strip = q('[data-bh-tabs]');
    const tabs = qa('[data-bh-tab]');
    const panes = qa('[data-bh-pane]');
    const cursor = q('[data-bh-cursor]');
    const legacySwitch = q('[data-bh-legacy]');
    const recentsRow = q('[data-bh-recents-row]');
    const recentsSwitch = q('[data-bh-recents]');
    const hiddenEmpty = q('[data-bh-empty]');
    const rowsIn = (sel) => new Map(qa(`${sel} [data-bh-name]`).map((el) => [el.dataset.bhName, el]));
    const generalRows = rowsIn('[data-bh-pane="general"]');
    const hiddenRows = rowsIn('[data-bh-pane="hidden"]');
    const groups = qa('[data-bh-group]');
    const tab = (name) => tabs.find((t) => t.dataset.bhTab === name);

    const copyMany = root.dataset.countMany || '[count] results';
    const copyOne = root.dataset.countOne || '[count] result';
    const copyDrop = root.dataset.countDrop || '[was] → [count] results';
    // category rows and tags, in the page's language (English as written in the plugin)
    const cats = {
      video: root.dataset.i18nCatVideo || CATEGORY.video,
      audio: root.dataset.i18nCatAudio || CATEGORY.audio,
      preset: root.dataset.i18nCatPreset || CATEGORY.preset,
    };
    const tagThird = root.dataset.i18nTagThird || '3rd party';
    const tagPreset = root.dataset.i18nTagPreset || 'custom preset';

    const hidden = new Set();
    let showLegacy = true;
    let showLegacyRecents = true;
    // the other Search tab switches: custom presets, third-party effects and their tags
    const OPTS = { presets: true, presetTag: true, third: true, thirdTag: true };
    const opts = { ...OPTS };
    const optSwitches = qa('[data-bh-opt]');
    const settings = q('[data-bh-settings]');
    let current = 'general';
    let mode = 'auto'; // 'auto' plays the loop; 'hands' is the visitor's
    let run = 0;
    let playing = false;
    let inView = false;
    const base = results(new Set(), ALL).length;

    /* ---- The bar's results: one layer per row, slid into place ---- */
    // the markup's rows are the still picture for no script; these replace them
    list.textContent = '';
    list.classList.add('is-live');
    const items = new Map();
    const px = (name, fallback) => parseFloat(getComputedStyle(root).getPropertyValue(name)) || fallback;

    const item = (key, effect) => {
      if (items.has(key)) return items.get(key);
      const el = document.createElement('span');
      if (!effect) {
        // the category rows are plain lines, as long as their names would be
        el.className = 'bh-item bh-cat';
        el.innerHTML = `<i class="bh-ln" style="--w: ${Math.min(120, 30 + cats[key.slice(4)].length * 5)}px"></i>`;
      } else {
        el.className = 'bh-item bh-row';
        let html = `<span class="bh-rname">${esc(effect.name)}</span>`;
        // the plugin shows one tag: 3rd party first, else custom preset
        if (effect.isThirdParty) html += `<span class="bh-tag bh-tag--third">${esc(tagThird)}</span>`;
        else if (effect.kind === 'preset') html += `<span class="bh-tag bh-tag--preset">${esc(tagPreset)}</span>`;
        el.innerHTML = html;
      }
      list.appendChild(el);
      items.set(key, el);
      return el;
    };

    const place = (el, y, animate) => {
      const fresh = !el.classList.contains('is-in');
      if (!fresh && animate) {
        el.style.setProperty('--y', `${y}px`);
        return;
      }
      el.style.transition = 'none';
      el.style.setProperty('--y', `${y}px`);
      if (!animate) el.classList.remove('is-out');
      el.classList.add('is-in');
      if (animate) {
        // comes back in from where hidden rows leave to
        el.classList.add('is-out');
        void el.offsetWidth;
        el.style.transition = '';
        el.classList.remove('is-out');
      } else {
        void el.offsetWidth;
        el.style.transition = '';
      }
    };

    const renderBar = (animate) => {
      const res = results(hidden, { legacy: showLegacy, presets: opts.presets, third: opts.third });
      list.classList.toggle('no-preset-tag', !opts.presetTag);
      list.classList.toggle('no-third-tag', !opts.thirdTag);
      const rowH = px('--bh-rh', 40);
      const catH = px('--bh-ch', 26);
      const shown = new Set();
      let y = 0;
      let kind = null;
      res.forEach((effect, index) => {
        if (effect.kind !== kind) {
          kind = effect.kind;
          const key = `cat:${kind}`;
          place(item(key), y, animate);
          shown.add(key);
          y += catH;
        }
        const el = item(effect.name, effect);
        place(el, y, animate);
        el.classList.toggle('is-selected', index === 0);
        shown.add(effect.name);
        y += rowH;
      });
      items.forEach((el, key) => {
        if (shown.has(key) || !el.classList.contains('is-in')) return;
        el.classList.remove('is-in', 'is-selected');
        if (animate) el.classList.add('is-out');
      });
      list.style.height = `${y}px`;
      scroller.style.height = `${Math.min(y, catH * 2 + rowH * 5)}px`;
      if (!animate) scroller.scrollTop = 0;

      if (countEl) {
        const n = res.length;
        const num = `<b class="bh-n">${n}</b>`;
        countEl.innerHTML =
          n < base
            ? esc(copyDrop).replace('[was]', `<span class="bh-was">${base}</span>`).replace('[count]', num)
            : esc(n === 1 ? copyOne : copyMany).replace('[count]', num);
      }
    };

    /* ---- The panel ---- */
    const setGone = (el, gone) => {
      el.classList.toggle('is-gone', gone);
      el.setAttribute('aria-hidden', String(gone));
    };

    const renderPanel = () => {
      generalRows.forEach((el, name) => setGone(el, hidden.has(name)));
      hiddenRows.forEach((el, name) => setGone(el, !hidden.has(name)));
      groups.forEach((group) => {
        const n = [...group.querySelectorAll('[data-bh-name]')].filter((el) => !el.classList.contains('is-gone')).length;
        const count = group.querySelector('[data-bh-gcount]');
        if (count) count.textContent = String(n);
        group.classList.toggle('is-empty', n === 0);
      });
      if (hiddenEmpty) hiddenEmpty.classList.toggle('is-on', hidden.size === 0);
      if (legacySwitch) {
        legacySwitch.classList.toggle('is-on', showLegacy);
        legacySwitch.setAttribute('aria-checked', String(showLegacy));
      }
      optSwitches.forEach((el) => {
        const on = opts[el.dataset.bhOpt];
        el.classList.toggle('is-on', on);
        el.setAttribute('aria-checked', String(on));
      });
      if (recentsRow) recentsRow.classList.toggle('is-disabled', !showLegacy);
      if (recentsSwitch) {
        recentsSwitch.classList.toggle('is-on', showLegacyRecents);
        recentsSwitch.setAttribute('aria-checked', String(showLegacyRecents));
        recentsSwitch.disabled = !showLegacy;
      }
    };

    const render = (animate) => {
      renderPanel();
      renderBar(animate);
    };

    // narrow panels scroll their tab row; keep the open tab in view
    const showTab = (el, smooth) => {
      if (!strip || !el) return;
      const pad = 26;
      let left = strip.scrollLeft;
      if (el.offsetLeft + el.offsetWidth > left + strip.clientWidth - pad) left = el.offsetLeft + el.offsetWidth - strip.clientWidth + pad;
      if (el.offsetLeft < left + 12) left = Math.max(0, el.offsetLeft - 12);
      if (left !== strip.scrollLeft) strip.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
    };

    const setTab = (name, smooth) => {
      current = name;
      tabs.forEach((t) => {
        const on = t.dataset.bhTab === name;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', String(on));
      });
      panes.forEach((p) => {
        const on = p.dataset.bhPane === name;
        p.classList.toggle('is-active', on);
        p.setAttribute('aria-hidden', String(!on));
      });
      showTab(tab(name), smooth);
    };

    /* ---- The visitor's clicks ---- */
    root.addEventListener('click', (event) => {
      const t = event.target.closest('[data-bh-tab], [data-bh-eye], [data-bh-unhide], [data-bh-legacy], [data-bh-recents], [data-bh-opt]');
      if (!t || !root.contains(t)) return;
      if (t.dataset.bhTab) setTab(t.dataset.bhTab, true);
      else if (t.hasAttribute('data-bh-eye')) {
        hidden.add(t.closest('[data-bh-name]').dataset.bhName);
        render(true);
      } else if (t.hasAttribute('data-bh-unhide')) {
        hidden.delete(t.closest('[data-bh-name]').dataset.bhName);
        render(true);
      } else if (t.hasAttribute('data-bh-legacy')) {
        showLegacy = !showLegacy;
        render(true);
      } else if (t.dataset.bhOpt) {
        opts[t.dataset.bhOpt] = !opts[t.dataset.bhOpt];
        render(true);
      } else if (t.hasAttribute('data-bh-recents') && showLegacy) {
        showLegacyRecents = !showLegacyRecents;
        renderPanel();
      }
    });

    /* ---- The loop ---- */
    const wait = (r, ms) =>
      new Promise((resolve, reject) => {
        window.setTimeout(() => (r === run && root.isConnected ? resolve() : reject(STOP)), ms);
      });
    const spot = (el, fx = 0.5, fy = 0.5) => {
      const s = stage.getBoundingClientRect();
      const b = el.getBoundingClientRect();
      return [b.left - s.left + b.width * fx, b.top - s.top + b.height * fy];
    };
    const setCursor = ([x, y]) => {
      cursor.style.setProperty('--cx', `${x}px`);
      cursor.style.setProperty('--cy', `${y}px`);
    };
    const moveTo = (r, el, fx, fy, ms = 620) => {
      setCursor(spot(el, fx, fy));
      return wait(r, ms);
    };
    const jumpTo = (el, fx, fy) => {
      cursor.style.transition = 'none';
      setCursor(spot(el, fx, fy));
      void cursor.offsetWidth;
      cursor.style.transition = '';
    };
    const tap = (r) => {
      cursor.classList.remove('is-click');
      void cursor.offsetWidth;
      cursor.classList.add('is-click');
      return wait(r, 180);
    };
    const clearPointer = () => {
      cursor.classList.remove('is-on', 'is-click');
      qa('.is-hover, .is-tip, .is-press').forEach((el) => el.classList.remove('is-hover', 'is-tip', 'is-press'));
    };

    const reset = () => {
      stage.classList.add('is-instant');
      hidden.clear();
      showLegacy = true;
      showLegacyRecents = true;
      Object.assign(opts, OPTS);
      if (settings) settings.scrollTop = 0;
      setTab('general', false);
      if (strip) strip.scrollLeft = 0;
      render(false);
      clearPointer();
      void stage.offsetWidth;
      stage.classList.remove('is-instant');
    };

    const hideOne = async (r, name) => {
      const row = generalRows.get(name);
      const eye = row.querySelector('[data-bh-eye]');
      await moveTo(r, row, 0.32, 0.55, 560);
      row.classList.add('is-hover');
      await moveTo(r, eye, 0.5, 0.6, 380);
      eye.classList.add('is-tip');
      await wait(r, 240);
      await tap(r);
      eye.classList.remove('is-tip');
      row.classList.remove('is-hover');
      hidden.add(name);
      render(true);
      await wait(r, 720);
    };

    const loop = async (r) => {
      await wait(r, 1300);
      const first = generalRows.get(NEVER[0]);
      jumpTo(first, 0.5, 3.2);
      cursor.classList.add('is-on');
      await wait(r, 200);
      for (const name of NEVER) await hideOne(r, name);

      // the Search tab: legacy effects off
      const searchTab = tab('search');
      await moveTo(r, searchTab, 0.5, 0.6, 620);
      await tap(r);
      setTab('search', true);
      await wait(r, 420);
      // scroll down to Legacy Effects, as you would in the panel
      if (settings) {
        const sec = legacySwitch.closest('.bh-sec');
        const top = sec.getBoundingClientRect().top - settings.getBoundingClientRect().top + settings.scrollTop - 8;
        settings.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        await wait(r, 520);
      }
      await moveTo(r, legacySwitch, 0.5, 0.55, 600);
      legacySwitch.classList.add('is-press');
      await tap(r);
      legacySwitch.classList.remove('is-press');
      showLegacy = false;
      render(true);
      await wait(r, 1300);

      // and where the hidden ones went
      const hiddenTab = tab('hidden');
      showTab(hiddenTab, true);
      await wait(r, 260);
      await moveTo(r, hiddenTab, 0.5, 0.6, 620);
      await tap(r);
      setTab('hidden', true);
      await wait(r, 900);
      cursor.classList.remove('is-on');
      await wait(r, 3000);

      // and again
      stage.classList.add('is-fade');
      await wait(r, 420);
      reset();
      stage.classList.remove('is-fade');
      await wait(r, 300);
    };

    const play = async () => {
      run += 1;
      const r = run;
      playing = true;
      reset();
      try {
        for (;;) await loop(r);
      } catch (error) {
        if (error !== STOP) throw error;
      }
    };

    const stop = () => {
      run += 1;
      playing = false;
      stage.classList.remove('is-fade');
      reset();
    };

    // the finished picture: three blurs hidden, legacy effects off
    const finished = () => {
      hidden.clear();
      NEVER.forEach((name) => hidden.add(name));
      showLegacy = false;
      setTab('hidden', false);
      render(false);
    };

    const handOver = () => {
      if (mode === 'hands') return;
      mode = 'hands';
      run += 1;
      playing = false;
      stage.classList.remove('is-fade', 'is-instant');
      clearPointer();
      root.classList.add('is-hands');
      // from now on the count speaks up when the visitor changes it
      if (countEl) countEl.setAttribute('aria-live', 'polite');
    };
    stage.addEventListener('pointerdown', (event) => event.isTrusted && handOver(), true);
    stage.addEventListener('focusin', handOver);

    render(false);

    if (still.matches || !('IntersectionObserver' in window)) {
      finished();
      handOver();
      return;
    }

    let io = null;
    const update = () => {
      if (!root.isConnected) {
        run += 1;
        if (io) io.disconnect();
        document.removeEventListener('visibilitychange', update);
        return;
      }
      if (mode === 'hands') return;
      const go = inView && !document.hidden;
      if (go && !playing) play();
      else if (!go && playing) stop();
    };

    io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          inView = entry.isIntersecting;
        });
        update();
      },
      { threshold: 0.35 }
    );
    io.observe(stage);
    document.addEventListener('visibilitychange', update);
  };

  const boot = () => document.querySelectorAll('[data-betterfx-hide]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
